import type { ColDef, ColGroupDef, GridApi, ICellRendererParams } from 'ag-grid-community'
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react'
import dayjs from 'dayjs'
import { type ReactNode, useMemo, useRef, useEffect } from 'react'
import { useSchedulePageFilters } from '../Context/Filters/SchedulePageFilterContext'
import {
  type ScheduleItemDatabaseKey,
  type ScheduleItemWithConflicts,
  getDayKey,
} from '../Context/ScheduleItems/scheduleItemStore'

import { LeftHeader } from './Headers/LeftHeader'
import { ShiftColumnHeader } from './Headers/ShiftColumnHeader'
import { TopToolbar } from './Headers/TopToolbar'
import './scheduleGrid.css'

type ScheduleGridProps<T extends { id: string }, R extends T> = {
  infoCell: (params: ICellRendererParams<R>) => ReactNode
  dayCell: (params: ICellRendererParams<R>) => ReactNode
  title?: string
  groupableColumns?: ColDef<T>[]
  primaryKey?: ScheduleItemDatabaseKey
  rows?: R[]
  isLoading?: boolean
}

/** notes
 *
 *  our data (flat schedule data), we want to:
 *    - group by either driver or tractor (and have the groups be expanded, custom rendered)
 *      - these groups are non-collapsible
 *    - optionally group these groups by depot/shift time
 *      - so we'd have depot > driver/tractor > shifts
 *      - these parent-groups should have custom renderers that
 *
 */

export function ScheduleGrid<T extends { id: string }, R extends T>({
  dayCell,
  infoCell,
  rows,
  groupableColumns,
  title = 'Driver Schedule',
  isLoading = false,
}: ScheduleGridProps<T, R>) {
  const gridApiRef = useRef<GridApi<T>>()

  useEffect(() => {
    if (isLoading) gridApiRef.current?.showLoadingOverlay()
    else gridApiRef?.current?.hideOverlay()
  }, [isLoading])

  const {
    datePickerType,
    dateRange: { startDate, endDate },
  } = useSchedulePageFilters()

  const dateColumns = useMemo(() => {
    const result: ColDef<T, ScheduleItemWithConflicts[]>[] = []
    const today = dayjs()
    for (let i = startDate; i.valueOf() < endDate.valueOf(); i = i.add(1, 'days')) {
      result.push({
        colId: i.toISOString(),
        cellRenderer: dayCell,
        headerName: i.format('ddd DD'),
        minWidth: 20,
        headerComponent: () => {
          return (
            <ShiftColumnHeader
              dayKey={getDayKey(i)}
              title={datePickerType === 'month' ? i.format('D') : i.format('ddd DD')}
              isToday={i.isSame(today, 'date')}
            />
          )
        },
        valueGetter: () => [],
      })
    }
    return result
  }, [startDate, endDate, datePickerType, dayCell])

  const columns = useMemo<Array<ColDef<T> | ColGroupDef<T>>>(
    () => [
      {
        colId: 'Main',
        width: 278,
        headerComponent: () => <LeftHeader title={title} />,
        cellRenderer: infoCell,
        suppressHeaderKeyboardEvent: () => true,

        suppressMovable: true,
        sortable: true,
        suppressFillHandle: true,
        suppressMenu: true,
        filter: true,
        flex: 0,
      },
      {
        colId: 'shifts',
        headerName: 'Shifts',
        suppressSpanHeaderHeight: true,
        headerGroupComponent: TopToolbar,
        children: dateColumns,
        minWidth: 400,
      },
      ...(groupableColumns ?? []),
    ],
    [dateColumns, groupableColumns, infoCell, title]
  )

  const gridOptions = useMemo<AgGridReactProps<T>>(
    () => ({
      suppressFocusAfterRefresh: true,
      suppressCellFocus: true,
      stopEditingWhenCellsLoseFocus: false,
      allowDragFromColumnsToolPanel: false,
      suppressMenuHide: true,
      suppressRowClickSelection: true,
      rowSelection: 'multiple' as const,
      domLayout: 'normal',
      getRowId: ({ data }) => data.id,
      groupHeaderHeight: 50,
      headerHeight: 120,
      getRowHeight: (params) => {
        // Check for group rows first
        if (params.node.group) {
          return datePickerType === 'week' ? 30 : 60
        }
        return 60
      },
      autoGroupColumnDef: {
        headerName: 'Grouping',
        width: 280,
        cellRendererSelector: (params) => ({
          component: params.node.group ? 'agGroupCellRenderer' : infoCell,
        }),
        cellStyle: { padding: '0px' },
      },
      suppressContextMenu: true,
      defaultColDef: {
        suppressMenu: true,
        resizable: false,
        flex: 1,
        suppressMovable: true,
        cellStyle: {
          padding: '0px',
        },
      },
      //suppressAggFuncInHeader: true,
      groupDisplayType: 'singleColumn',
    }),
    [datePickerType, infoCell]
  )

  return (
    <div className='ag-theme-alpine schedule-grid'>
      <AgGridReact
        rowData={rows?.length ? rows : null}
        gridOptions={gridOptions}
        columnDefs={columns}
        onGridReady={(e) => {
          gridApiRef.current = e.api
        }}
      />
    </div>
  )
}
